import { Flex, Grid } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import {
  FormLabel,
  ISelectChangeOptions,
  Input,
  ModalBody,
  ModalContent,
  ModalFooterDefault,
  ModalHeader,
  Select,
  SelectOption,
  useSnackbar,
} from 'ui-components';
import {
  STEP,
  useInviteCreatorStore,
} from '~/components/CreatorInvitationModal/useInviteCreatorStore';
import { FEATURE_FLAGS } from '~/constants/featureFlags';
import { capitalizeFirstLetter } from '~/core/utils';
import { trackEvent } from '~/core/utils/trackEvent';
import { ICreatorOnPlatformCreator } from '~/hooks/api/useGetOnPlatformCreatorByEmail';
import {
  PlatformData,
  PlatformSearchResult,
  useOPCreatorSearch,
} from '~/hooks/api/useOPCreatorSearch';
import {
  SmartAccountsType,
  useSearchSmartProfiles,
} from '~/hooks/api/useSearchSmartProfiles';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { getPlatformKey, useAddTalentStore } from '~/screens/AddTalent';
import { EnableSmartProfile } from '~/screens/AddTalent/SmartProfile';
import { PLATFORMS, Platform } from '~/screens/AddTalent/constants';
import { parseHandle } from '~/screens/ManagePlatforms';

export const SearchHandle = ({
  searchOnPlatform,
}: {
  searchOnPlatform: boolean;
}) => {
  const snackbar = useSnackbar();

  const { isEnabled: isFoamAddOnPlatformEnabled } = useFeature(
    FEATURE_FLAGS.foamAddOnPlatform,
  );

  const {
    goToNextStep,
    searchedHandle,
    searchedPlatform,
    setSearchedHandle,
    setSearchResult,
    setSearchedPlatform,
    setPlatforms,
    smartProfileEnabled,
    setSmartProfileEnabled,
    setSmartProfileRequestId,
    reset,
    resetSmartProfileState,
    setIsModalOpen,
  } = useAddTalentStore();

  const { openModal: onOpenInviteCreatorModal } = useInviteCreatorStore();

  const searchOPCreators = useOPCreatorSearch();
  const searchSmartProfiles = useSearchSmartProfiles();

  const [platform, setPlatform] = useState<Platform | undefined>(
    (searchedPlatform as Platform) ?? 'instagram',
  );
  const [handle, setHandle] = useState<string>(
    searchedHandle ? searchedHandle : '',
  );

  const pipelineStatus = useMemo(
    // @ts-ignore
    () => searchOPCreators.data?.[platform]?.pipelineStatus,
    [searchOPCreators.data, platform],
  );

  const validationError = useMemo(() => {
    if (!handle) return undefined;

    const validations = [
      // Check for whitespace in handle
      {
        condition: /\s/.test(handle),
        message: 'Spaces are not allowed.',
      },
      // Check for duplicate handles in the agency (foam_add_on_platform)
      {
        condition: Boolean(
          isFoamAddOnPlatformEnabled &&
            (searchOPCreators.error as AxiosError)?.response?.status === 409,
        ),
        message: 'Talent already exists in the agency.',
      },
      // Check for URL/handle format errors
      {
        condition: searchOPCreators.error,
        message: `${capitalizeFirstLetter(
          platform as string,
        )} handle or URL is in the wrong format.`,
      },
      // Check for duplicate handles in the agency
      {
        condition: Boolean(
          platform &&
            parseHandle(handle) === searchOPCreators.data?.[platform]?.handle &&
            pipelineStatus === 'duplicated',
        ),
        message: 'Talent already exists in the agency.',
      },
    ];

    // Return the first validation error message that applies
    const error = validations.find((validation) => validation.condition);
    return error?.message;
  }, [
    handle,
    pipelineStatus,
    platform,
    searchOPCreators.data,
    searchOPCreators.error,
    isFoamAddOnPlatformEnabled,
  ]);

  const isLoading =
    searchOPCreators.isLoading ||
    (smartProfileEnabled && searchSmartProfiles.isLoading);

  const handleSearchSuccess = (
    handle: string,
    platform: Platform,
    resp: PlatformSearchResult,
    smartPlatforms?: SmartAccountsType[],
  ) => {
    setSearchedHandle(handle);
    setSearchedPlatform(platform);
    setPlatforms([
      {
        ...resp[platform],
        platform: platform,
        type: 'search-result',
      } as PlatformData,
      ...(smartPlatforms || []),
    ]);
    setSearchResult(resp[platform]);
    goToNextStep();
  };

  const verifyHandle = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!handle || !platform) return;

    const platformKey = getPlatformKey(platform);
    const performSearchOPCreator = (smartProfileData?: SmartAccountsType[]) => {
      const searchParams = {
        [platformKey]: handle,
      };

      if (smartProfileData) {
        smartProfileData.forEach((profile) => {
          const key = getPlatformKey(profile.platform as Platform);
          if (key !== platformKey) {
            searchParams[key] = profile.handle;
          }
        });
      }
      // originalPlatform is needed for foam_add_on_platform flow
      if (searchOnPlatform && !!platform) {
        searchParams.originalPlatform = platform;
      }
      searchOPCreators.mutate(searchParams, {
        onSuccess: (resp) => {
          // @ts-ignore - Somebody make this type-safe?
          const pipelineStatus = resp[platform]?.pipelineStatus;

          // Check whether pipeline status is successful
          if (pipelineStatus === 'duplicated') {
            // Handle error
            return;
          }

          // add existing talent flow
          if (searchOnPlatform) {
            const onPlatformCreator = resp[platform];
            const matchedCreator = !!onPlatformCreator?.id;
            if (matchedCreator) {
              setIsModalOpen(false);
              const {
                id,
                name,
                handle,
                profilePictureUrl: avatar,
                isManaged,
              } = onPlatformCreator;

              onOpenInviteCreatorModal({
                creatorOnPlatformResult: {
                  id,
                  name,
                  handle,
                  avatar,
                  isManaged,
                  searchedPlatform: platform,
                  platforms: resp,
                } as ICreatorOnPlatformCreator,
                isAddTalentFlow: true,
                step: isManaged
                  ? STEP.addTalentManaged
                  : STEP.addTalentUnmanaged,
              });
              trackEvent(
                isManaged
                  ? 'foam-add-talent-found-managed'
                  : 'foam-add-talent-found-unmanaged',
              );

              return; // Break the regular flow
            } else {
              // If there are no matches, the original flow continues
              trackEvent('foam-add-talent-not-found');
            }
          }

          // Filter out the ones with pipelineStatus: "duplicated"
          const filteredPlatforms = Object.fromEntries(
            Object.entries(resp).filter(
              ([_key, value]) => value.pipelineStatus !== 'duplicated',
            ),
          );

          // Filter out the ones with pipelineStatus: "duplicated" from smart profiles
          const filteredSmartProfiles = smartProfileData?.filter(
            (item) =>
              resp[item.platform as keyof PlatformSearchResult]
                ?.pipelineStatus !== 'duplicated',
          );

          const filteredSmartPlatforms = filteredSmartProfiles?.map((item) => {
            const platformData = filteredPlatforms[item.platform];
            if (platformData && platformData.status === 'manual') {
              return {
                ...item,
                followersCount: undefined,
                profilePictureUrl: undefined,
              };
            }
            return item;
          });

          handleSearchSuccess(
            handle,
            platform,
            filteredPlatforms,
            filteredSmartPlatforms,
          );
        },
      });
    };

    if (smartProfileEnabled) {
      searchSmartProfiles.mutate(
        {
          handle,
          platform,
        },
        {
          onSuccess: (response) => {
            const smartPlatforms = response.map((resp) => {
              return {
                ...resp,
                type: 'smart-profile',
              };
            });
            performSearchOPCreator(smartPlatforms);
          },
          onError: (error) => {
            if (error.code === 'ECONNABORTED') {
              performSearchOPCreator();
              return;
            }
            snackbar.error({
              description: 'Something went wrong',
            });
          },
        },
      );

      return;
    }
    performSearchOPCreator();
  };

  const handleToggle = (toggle: boolean) => {
    setSmartProfileEnabled(toggle);
    if (!toggle) {
      setSmartProfileRequestId(undefined);
    }
  };

  const handlePlatformSelection: (options: ISelectChangeOptions) => void =
    useCallback(
      (options) => {
        if (options.value === PLATFORMS.snapchat) {
          setSmartProfileEnabled(false);
        }

        // reset the error field when the platform changes
        searchOPCreators.reset();
        setPlatform(options.value as Platform);
      },
      [setSmartProfileEnabled, searchOPCreators],
    );

  return (
    <form onSubmit={verifyHandle}>
      <ModalContent>
        <ModalHeader>Choose a platform and social handle</ModalHeader>
        <ModalBody>
          <Flex direction="column" gap={4}>
            <Flex gap={6} alignItems="flex-start">
              <Grid gap={1}>
                <FormLabel>Platform</FormLabel>
                <Select
                  placeholder="Select"
                  value={platform}
                  onChange={handlePlatformSelection}
                  isDisabled={isLoading}
                  trackId="foam-add-talent-search-handle-platform-select"
                >
                  <SelectOption value="instagram">Instagram</SelectOption>
                  <SelectOption value="snapchat">Snapchat</SelectOption>
                  <SelectOption value="tiktok">TikTok</SelectOption>
                  <SelectOption value="youtube">YouTube</SelectOption>
                </Select>
              </Grid>
              <Grid gap={1} flex={1}>
                <FormLabel>Handle</FormLabel>
                <Input
                  size="lg"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  aria-label="Talent Handle"
                  aria-describedby="handle-hint"
                  isRequired
                  aria-required="true"
                  isInvalid={Boolean(validationError)}
                  aria-invalid={Boolean(validationError)}
                  error={validationError}
                  clearButtonProps={{
                    'aria-label': 'Handle',
                    trackId: 'foam-add-talent-search-handle-input-clear',
                    onClick: () => {
                      setHandle('');
                      searchOPCreators.reset();
                    },
                  }}
                  withClearButton={handle ? true : false}
                  placeholder={'@handle or URL'}
                  trackId="foam-add-talent-search-handle-input"
                />
              </Grid>
            </Flex>
            <EnableSmartProfile
              isToggled={smartProfileEnabled}
              onToggle={handleToggle}
              isDisabled={isLoading || platform === PLATFORMS.snapchat}
              tooltip={
                platform === PLATFORMS.snapchat
                  ? 'Smart Profiles are not supported for Snapchat. Please use a different handle'
                  : undefined
              }
            />
          </Flex>
        </ModalBody>
        <ModalFooterDefault
          primary={{
            ariaLabel: 'next',
            type: 'submit',
            title: isLoading ? 'Verifying...' : 'Next',
            isDisabled:
              !platform || !handle || isLoading || Boolean(validationError),
            size: 'md',
            trackId: 'foam-add-talent-search-handle-next',
          }}
          secondary={{
            ariaLabel: 'cancel',
            onClick: () => {
              reset();
              // This setTiemout is added to fix [FOAM-10397]. The issue occurs when updating `smartProfileEnabled`-
              // the `onChange` method of the `Toggle` gets triggered, and internally, the `@ark-ui` library
              // inverts the state (`!isChecked`). To ensure the toggle updated correctly,
              // we first reset the entire state and then explicity update the `smartProfileEnabled` separately.
              setTimeout(() => {
                resetSmartProfileState();
              }, 0);
            },
            type: 'reset',
            title: 'Cancel',
            isDisabled: isLoading,
            size: 'md',
            trackId: 'foam-add-talent-search-handle-cancel',
          }}
        />
      </ModalContent>
    </form>
  );
};
